<template>
    <v-card>
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="ml-n3 mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h2 class="font-weight-regular">
                Register Quality
            </h2>
        </v-card-title>
        <v-divider class="mx-6" />
        <v-card-text
            :style="{ 'max-height': `${height}px`, 'overflow-y': 'auto' }"
        >
            <v-form ref="qualityForm" v-model="valid">
                <v-data-table
                    :headers="headers"
                    :items="filteredItems"
                    class="elevation-0 mt-7"
                    :mobile-breakpoint="0"
                    :loading="loading"
                    disable-pagination
                    hide-default-footer
                >
                    <template v-slot:top>
                        <v-row
                            no-gutter
                            class="mt-0 mx-0 pt-4 pb-0 pb-4"
                            :style="{ 'background-color': '#eeeeee' }"
                        >
                            <v-col cols="12" sm="6" class="d-flex align-center">
                                <h2 class="my-n3 d-flex align-center">
                                    ITEMS
                                </h2>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-combobox
                                    v-model="selectedProcess"
                                    :items="processOptions"
                                    item-text="name"
                                    item-value="id"
                                    label="Process"
                                    dense
                                    clearable
                                    :return-object="true"
                                    :rules="[rules.required]"
                                    @change="handleProcessChange"
                                />
                            </v-col>
                        </v-row>
                    </template>
                    <template v-slot:[`item.index`]="{ index }">
                        <p class="my-0">
                            {{ index + 1 }}
                        </p>
                    </template>
                    <template v-slot:[`item.partNumber`]="{ item }">
                        <div class="d-flex justify-center">
                            <p class="my-0">
                                {{ item.partNumber }}
                            </p>
                        </div>
                    </template>
                    <template v-slot:[`item.description`]="{ item }">
                        <p class="my-0">
                            {{ item.description }}
                        </p>
                    </template>
                    <template v-slot:[`item.pendingQty`]="{ item }">
                        <div class="d-flex justify-center">
                            <p class="my-0">
                                {{ getPendingQty(item) }}
                            </p>
                        </div>
                    </template>
                    <template v-slot:[`item.reprocessQty`]="{ item }">
                        <v-text-field
                            v-if="selectedProcess"
                            v-model="
                                item.processAssignments[selectedProcess.id]
                                    .reprocessQty
                            "
                            type="text"
                            dense
                            :rules="[() => maxValue(item)]"
                            @change="onReprocessChange(item)"
                        />
                    </template>
                    <template v-slot:[`item.reprocessReason`]="{ item }">
                        <v-combobox
                            v-if="selectedProcess"
                            v-model="
                                item.processAssignments[selectedProcess.id]
                                    .reprocessReason
                            "
                            :items="ncCodes"
                            hide-selected
                            dense
                            flat
                            item-text="longName"
                            :disabled="!hasReprocessQty(item)"
                            :rules="[() => reprocessReasonRule(item)]"
                        />
                    </template>
                    <template v-slot:[`item.destinationProcess`]="{ item }">
                        <v-combobox
                            v-if="selectedProcess"
                            v-model="
                                item.processAssignments[selectedProcess.id]
                                    .destinationProcess
                            "
                            :items="getPreviousProcesses(item)"
                            item-text="name"
                            item-value="id"
                            dense
                            :disabled="
                                !hasReprocessQty(item) ||
                                    getPreviousProcesses(item).length <= 0
                            "
                            :rules="[() => destinationProcessRule(item)]"
                        />
                    </template>
                    <template v-slot:[`item.rejectQty`]="{ item }">
                        <v-text-field
                            v-if="selectedProcess"
                            v-model="
                                item.processAssignments[selectedProcess.id]
                                    .rejectQty
                            "
                            type="text"
                            dense
                            :rules="[() => maxValue(item)]"
                            @change="onRejectChange(item)"
                        />
                    </template>
                    <template v-slot:[`item.NCcode`]="{ item }">
                        <v-combobox
                            v-if="selectedProcess"
                            v-model="
                                item.processAssignments[selectedProcess.id]
                                    .NCcode
                            "
                            :items="ncCodes"
                            hide-selected
                            dense
                            flat
                            item-text="longName"
                            :disabled="!hasRejectQty(item)"
                            :rules="[() => ncCodeRule(item)]"
                        />
                    </template>
                    <template v-slot:[`item.releaseQty`]="{ item }">
                        <div v-if="shouldShowReleaseField(item)" class="mb-5">
                            <v-text-field
                                v-model="
                                    item.processAssignments[selectedProcess.id]
                                        .releaseQty
                                "
                                type="text"
                                dense
                                :rules="[() => maxValue(item)]"
                            />
                        </div>
                        <div v-else class="d-flex justify-center">
                            <p class="my-0">-</p>
                        </div>
                    </template>
                </v-data-table>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-simple-checkbox
                v-if="showReleaseColumn"
                color="primary"
                v-model="setAvailableAsReleased"
                @click="setAllAvailableAsReleased"
                class="ml-2 d-flex justify-center align-center"
            >
            </v-simple-checkbox>
            <small v-if="showReleaseColumn" class="ml-2">
                Select All As Released
            </small>
            <v-spacer />
            <v-btn
                :loading="loading"
                color="primary"
                rounded
                class="mb-3 mr-3"
                :disabled="disableButton()"
                @click="openConfirmDialog"
            >
                NEXT
            </v-btn>
        </v-card-actions>
        <v-dialog
            :retain-focus="false"
            v-model="confirmDialog"
            persistent
            max-width="1100px"
        >
            <v-card>
                <v-card-title>
                    <v-btn
                        small
                        icon
                        @click="closeConfirmDialog"
                        color="primary"
                        class="ml-n3 mr-2"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <h2 class="font-weight-regular">
                        Confirm Quality Process
                    </h2>
                </v-card-title>
                <v-divider class="mx-6" />
                <v-card-text>
                    <v-form>
                        <v-data-table
                            :headers="confirmHeaders"
                            :items="itemsForConfirmation"
                            class="elevation-0 mt-7"
                            :mobile-breakpoint="0"
                            :loading="loading"
                            disable-pagination
                            hide-default-footer
                            :style="{
                                'max-height': `${height}px`,
                                'overflow-y': 'auto',
                            }"
                        >
                            <template v-slot:top>
                                <v-row
                                    no-gutter
                                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                                    :style="{ 'background-color': '#eeeeee' }"
                                >
                                    <v-col
                                        cols="12"
                                        class="d-flex align-center"
                                    >
                                        <h2 class="my-n3 d-flex align-center">
                                            ITEMS
                                        </h2>
                                    </v-col>
                                </v-row>
                            </template>
                            <template v-slot:[`item.partNumber`]="{ item }">
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.partNumber }}
                                    </p>
                                </div>
                            </template>
                            <template v-slot:[`item.releaseQty`]="{ item }">
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.releaseQty || '-' }}
                                    </p>
                                </div>
                            </template>
                            <template v-slot:[`item.reprocessQty`]="{ item }">
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.reprocessQty || '-' }}
                                    </p>
                                </div>
                            </template>
                            <template
                                v-slot:[`item.reprocessReason`]="{ item }"
                            >
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.reprocessReason || '-' }}
                                    </p>
                                </div>
                            </template>
                            <template
                                v-slot:[`item.destinationProcess`]="{ item }"
                            >
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.destinationProcess || '-' }}
                                    </p>
                                </div>
                            </template>
                            <template v-slot:[`item.rejectQty`]="{ item }">
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.rejectQty || '-' }}
                                    </p>
                                </div>
                            </template>
                            <template v-slot:[`item.NCcode`]="{ item }">
                                <div class="d-flex justify-center">
                                    <p class="my-0">
                                        {{ item.NCcode || '-' }}
                                    </p>
                                </div>
                            </template>
                        </v-data-table>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pt-0 pb-5">
                    <v-row class="ma-2">
                        <v-spacer />
                        <v-col cols="3" class="d-flex justify-end pa-0">
                            <v-btn
                                color="primary"
                                rounded
                                :disabled="!valid"
                                :loading="loading"
                                @click="registerQuality"
                            >
                                SAVE
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import _ from 'lodash'
import API from '@/services/api'

export default {
    name: 'MultipleQualityPerProcess',
    props: {
        workOrder: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        valid: false,
        loading: false,
        height: 0,
        selectedProcess: undefined,
        rules: {
            required: v => !!v || 'The value is required',
        },
        baseHeaders: [
            {
                text: 'ID',
                value: 'index',
                align: 'center',
                sortable: false,
                width: '50',
            },
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'PENDING',
                value: 'pendingQty',
                align: 'center',
                sortable: false,
                width: '100',
            },
            {
                text: 'RELEASE',
                value: 'releaseQty',
                align: 'center',
                sortable: false,
                width: '120',
            },
            {
                text: 'REPROCESS',
                value: 'reprocessQty',
                align: 'center',
                sortable: false,
                width: '140',
            },
            {
                text: 'REPROCESS REASON',
                value: 'reprocessReason',
                align: 'center',
                sortable: false,
                width: '180',
            },
            {
                text: 'RETURN TO',
                value: 'destinationProcess',
                align: 'center',
                sortable: false,
                width: '170',
            },
            {
                text: 'REJECT',
                value: 'rejectQty',
                align: 'center',
                sortable: false,
                width: '120',
            },
            {
                text: 'NC CODE',
                value: 'NCcode',
                align: 'center',
                sortable: false,
                width: '170',
            },
        ],
        confirmHeaders: [
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'RELEASE',
                value: 'releaseQty',
                align: 'center',
                sortable: false,
                width: '100',
            },
            {
                text: 'REPROCESS',
                value: 'reprocessQty',
                align: 'center',
                sortable: false,
                width: '100',
            },
            {
                text: 'REPROCESS REASON',
                value: 'reprocessReason',
                align: 'center',
                sortable: false,
                width: '150',
            },
            {
                text: 'RETURN TO',
                value: 'destinationProcess',
                align: 'center',
                sortable: false,
                width: '150',
            },
            {
                text: 'REJECT',
                value: 'rejectQty',
                align: 'center',
                sortable: false,
                width: '100',
            },
            {
                text: 'NC CODE',
                value: 'NCcode',
                align: 'center',
                sortable: false,
                width: '150',
            },
        ],
        ncCodes: [],
        rejectionReasons: [],
        processDefinitions: [],
        qualityProcess: {
            id: 'quality',
            name: 'Quality',
        },
        excludedProcessIds: ['packing'],
        settingProcessesId: '',
        confirmDialog: false,
        assignments: {},
        setAvailableAsReleased: false,
    }),
    computed: {
        items() {
            const workOrderItems = Array.isArray(this.workOrder?.items)
                ? this.workOrder.items
                : []

            return workOrderItems.map(item => {
                const copy = _.cloneDeep(item)
                const processesInfo = this.buildProcessesInfo(copy)
                const processAssignments = {}

                processesInfo.forEach(process => {
                    processAssignments[process.id] = this.ensureAssignment(
                        copy.partNumberId,
                        process.id
                    )
                })

                return {
                    ...copy,
                    processesInfo,
                    processAssignments,
                }
            })
        },
        processOptions() {
            const options = {}

            this.items.forEach(item => {
                item.processesInfo.forEach(process => {
                    if (process.id !== this.qualityProcess.id) {
                        options[process.id] = {
                            id: process.id,
                            name: process.name,
                        }
                    }
                })
            })

            return Object.values(options).sort((a, b) =>
                a.name.localeCompare(b.name)
            )
        },
        headers() {
            if (this.showReleaseColumn) {
                return this.baseHeaders
            }
            return this.baseHeaders.filter(
                header => header.value !== 'releaseQty'
            )
        },
        filteredItems() {
            const processId = this.selectedProcess?.id

            if (!processId) {
                return this.items.filter(item =>
                    item.processesInfo.some(process => process.pendingQty > 0)
                )
            }

            return this.items.filter(item => {
                const processInfo = this.getProcessInfo(item, processId)
                return !!processInfo && Number(processInfo.pendingQty || 0) > 0
            })
        },
        itemsForConfirmation() {
            return this.itemsToSubmit().map(entry => ({
                partNumber: entry.item.partNumber,
                releaseQty: entry.releaseQty || undefined,
                reprocessQty: entry.reprocessQty || undefined,
                reprocessReason: entry.reprocessReasonName,
                destinationProcess: entry.destinationProcessName,
                rejectQty: entry.rejectQty || undefined,
                NCcode: entry.ncCodeName,
            }))
        },
        showReleaseColumn() {
            const processId = this.selectedProcess?.id

            if (!processId) {
                return false
            }
            return this.filteredItems.some(item =>
                this.shouldShowReleaseFieldForProcess(item, processId)
            )
        },
    },
    watch: {
        selectedProcess() {
            this.resetProcessValidations()
            this.verifyCheckBox()
        },
        processOptions(newOptions) {
            if (!Array.isArray(newOptions) || newOptions.length === 0) {
                this.selectedProcess = undefined
                return
            }

            const currentId = this.selectedProcess?.id
            const exists = currentId
                ? newOptions.some(option => option.id === currentId)
                : false

            if (currentId && !exists) {
                this.selectedProcess = undefined
            }
        },
        workOrder: {
            handler() {
                this.assignments = {}
                this.selectedProcess = undefined
            },
            immediate: false,
        },
        filteredItems: {
            handler() {
                this.verifyCheckBox()
            },
            deep: true,
        },
    },
    async mounted() {
        try {
            this.loading = true
            await this.initializeSettings()
            this.onResize()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ensureAssignment(partNumberId, processId) {
            if (!partNumberId || !processId) {
                return {
                    releaseQty: undefined,
                    reprocessQty: undefined,
                    rejectQty: undefined,
                    reprocessReason: undefined,
                    destinationProcess: undefined,
                    NCcode: undefined,
                }
            }

            if (!this.assignments[partNumberId]) {
                this.$set(this.assignments, partNumberId, {})
            }

            if (!this.assignments[partNumberId][processId]) {
                this.$set(this.assignments[partNumberId], processId, {
                    releaseQty: undefined,
                    reprocessQty: undefined,
                    rejectQty: undefined,
                    reprocessReason: undefined,
                    destinationProcess: undefined,
                    NCcode: undefined,
                })
            }

            return this.assignments[partNumberId][processId]
        },
        async initializeSettings() {
            const {
                data: { settings },
            } = await API.getSettings()

            const processSetting = settings.find(
                setting => setting.name.toLowerCase() === 'processes'
            )

            if (processSetting) {
                this.processDefinitions = processSetting.processes || []
                this.settingProcessesId = processSetting.id
            }

            const qualitySetting = settings.find(
                setting => setting.name.toLowerCase() === 'quality'
            )

            if (qualitySetting) {
                this.ncCodes = _.cloneDeep(qualitySetting.ncCodes || [])
                this.ncCodes.forEach(code => {
                    code.longName = code.description
                        ? `${code.code} - (${code.description})`
                        : `${code.code}`
                })
                this.rejectionReasons = _.cloneDeep(
                    qualitySetting.rejectionReasons || []
                )
            }
        },
        buildProcessesInfo(item) {
            const orderedProcessIds = Array.isArray(item?.processes)
                ? item.processes
                : []

            if (orderedProcessIds.length === 0) {
                return []
            }

            const productionEntries = Array.isArray(item?.production)
                ? item.production
                : []
            const qualityEntries = Array.isArray(item?.quality)
                ? item.quality
                : []

            return orderedProcessIds
                .map((processId, index) => {
                    let processDefinition

                    if (this.excludedProcessIds.includes(processId)) {
                        return undefined
                    }

                    if (processId === this.qualityProcess.id) {
                        processDefinition = this.qualityProcess
                    } else {
                        processDefinition =
                            this.processDefinitions.find(
                                process => process.id === processId
                            ) ||
                            (processId
                                ? { id: processId, name: processId }
                                : undefined)
                    }

                    if (!processDefinition) {
                        return undefined
                    }

                    const totalProduced = productionEntries
                        .filter(entry => entry.process?.id === processId)
                        .reduce(
                            (accumulator, entry) =>
                                accumulator + Number(entry.qty || 0),
                            0
                        )

                    const totalReleased = qualityEntries
                        .filter(entry => entry.selectedProcessId === processId)
                        .reduce(
                            (accumulator, entry) =>
                                accumulator + Number(entry.releaseQty || 0),
                            0
                        )

                    const totalRejected = qualityEntries
                        .filter(entry => entry.selectedProcessId === processId)
                        .reduce(
                            (accumulator, entry) =>
                                accumulator + Number(entry.rejectQty || 0),
                            0
                        )

                    const pendingQty = Math.max(
                        0,
                        totalProduced - totalReleased - totalRejected
                    )

                    return {
                        id: processDefinition.id,
                        name: processDefinition.name,
                        pendingQty,
                        index,
                        nextProcessId: orderedProcessIds[index + 1],
                        previousProcessIds: orderedProcessIds.slice(
                            0,
                            index + 1
                        ),
                        assemblyProcess: processDefinition.assemblyProcess,
                    }
                })
                .filter(Boolean)
        },
        getProcessInfo(item, processId = this.selectedProcess?.id) {
            if (!processId || !item?.processesInfo) {
                return undefined
            }
            return item.processesInfo.find(process => process.id === processId)
        },
        getPendingQty(item) {
            if (!this.selectedProcess?.id) {
                return item.processesInfo.reduce(
                    (accumulator, process) =>
                        accumulator + Number(process.pendingQty || 0),
                    0
                )
            }

            const processInfo = this.getProcessInfo(item)
            return processInfo ? processInfo.pendingQty : 0
        },
        getPreviousProcesses(item) {
            const processInfo = this.getProcessInfo(item)

            if (!processInfo) {
                return []
            }

            return processInfo.previousProcessIds
                .map(processId =>
                    item.processesInfo.find(process => process.id === processId)
                )
                .filter(Boolean)
        },
        getNextProcess(item, processId = this.selectedProcess?.id) {
            const processInfo = this.getProcessInfo(item, processId)

            if (!processInfo || !processInfo.nextProcessId) {
                return undefined
            }

            return item.processesInfo.find(
                process => process.id === processInfo.nextProcessId
            )
        },
        shouldShowReleaseField(item) {
            if (!this.selectedProcess?.id) {
                return false
            }

            return this.shouldShowReleaseFieldForProcess(
                item,
                this.selectedProcess.id
            )
        },
        shouldShowReleaseFieldForProcess(item, processId) {
            const processInfo = this.getProcessInfo(item, processId)
            const nextProcess = this.getNextProcess(item, processId)

            if (!processInfo) {
                return false
            }

            return nextProcess?.id === this.qualityProcess.id
        },
        maxValue(item) {
            if (!this.selectedProcess?.id) {
                return true
            }
            const assignment = item.processAssignments[this.selectedProcess.id]

            if (!assignment) {
                return true
            }

            const keys = ['releaseQty', 'reprocessQty', 'rejectQty']

            for (const key of keys) {
                const value = assignment[key]
                if (value !== undefined && value !== null && value !== '') {
                    const numericValue = Number(value)

                    if (Number.isNaN(numericValue)) {
                        return 'must be a valid number.'
                    }

                    if (numericValue < 0) {
                        return 'cannot be negative.'
                    }
                }
            }

            const total =
                Number(assignment.releaseQty || 0) +
                Number(assignment.reprocessQty || 0) +
                Number(assignment.rejectQty || 0)

            const pendingQty = this.getPendingQty(item)

            if (total > pendingQty) {
                return `The sum of all the quantities cannot be greater than ${pendingQty}.`
            }

            return true
        },
        reprocessReasonRule(item) {
            if (!this.selectedProcess?.id) {
                return true
            }

            const assignment = item.processAssignments[this.selectedProcess.id]

            if (!assignment) {
                return true
            }

            if (Number(assignment.reprocessQty || 0) > 0) {
                return assignment.reprocessReason
                    ? true
                    : 'You must select a reprocess reason.'
            }

            return true
        },
        destinationProcessRule(item) {
            if (!this.selectedProcess?.id) {
                return true
            }

            const assignment = item.processAssignments[this.selectedProcess.id]

            if (!assignment) {
                return true
            }

            if (Number(assignment.reprocessQty || 0) > 0) {
                return assignment.destinationProcess
                    ? true
                    : 'You must select a destination process.'
            }

            return true
        },
        ncCodeRule(item) {
            if (!this.selectedProcess?.id) {
                return true
            }

            const assignment = item.processAssignments[this.selectedProcess.id]

            if (!assignment) {
                return true
            }

            if (Number(assignment.rejectQty || 0) > 0) {
                return assignment.NCcode ? true : 'You must enter a NC code.'
            }

            return true
        },
        hasReprocessQty(item) {
            if (!this.selectedProcess?.id) {
                return false
            }
            const assignment = item.processAssignments[this.selectedProcess.id]
            return Number(assignment?.reprocessQty || 0) > 0
        },
        hasRejectQty(item) {
            if (!this.selectedProcess?.id) {
                return false
            }
            const assignment = item.processAssignments[this.selectedProcess.id]
            return Number(assignment?.rejectQty || 0) > 0
        },
        onReprocessChange(item) {
            if (!this.selectedProcess?.id) {
                return
            }
            const assignment = item.processAssignments[this.selectedProcess.id]

            if (Number(assignment.reprocessQty || 0) <= 0) {
                assignment.reprocessReason = undefined
                assignment.destinationProcess = undefined
            }
        },
        onRejectChange(item) {
            if (!this.selectedProcess?.id) {
                return
            }
            const assignment = item.processAssignments[this.selectedProcess.id]

            if (Number(assignment.rejectQty || 0) <= 0) {
                assignment.NCcode = undefined
            }
        },
        itemsToSubmit() {
            if (!this.selectedProcess?.id) {
                return []
            }

            return this.filteredItems
                .map(item => {
                    const assignment =
                        item.processAssignments[this.selectedProcess.id]

                    if (!assignment) {
                        return undefined
                    }

                    const releaseQty = Number(assignment.releaseQty || 0)
                    const reprocessQty = Number(assignment.reprocessQty || 0)
                    const rejectQty = Number(assignment.rejectQty || 0)
                    const total = releaseQty + reprocessQty + rejectQty

                    if (total <= 0) {
                        return undefined
                    }

                    const destination = assignment.destinationProcess
                    const reprocessReason = assignment.reprocessReason
                    const ncCode = assignment.NCcode

                    return {
                        item,
                        releaseQty: releaseQty > 0 ? releaseQty : undefined,
                        reprocessQty:
                            reprocessQty > 0 ? reprocessQty : undefined,
                        rejectQty: rejectQty > 0 ? rejectQty : undefined,
                        destinationProcessId: destination
                            ? destination.id
                            : undefined,
                        destinationProcessName: destination
                            ? destination.name
                            : undefined,
                        reprocessReasonId: reprocessReason
                            ? reprocessReason.id
                            : undefined,
                        reprocessReasonName: reprocessReason
                            ? reprocessReason.code
                            : undefined,
                        ncCodeId: ncCode ? ncCode.id : undefined,
                        ncCodeName: ncCode ? ncCode.code : undefined,
                    }
                })
                .filter(Boolean)
        },
        disableButton() {
            if (!this.selectedProcess?.id) {
                return true
            }

            if (!this.filteredItems.length) {
                return true
            }

            const itemsToSubmit = this.itemsToSubmit()

            if (itemsToSubmit.length === 0) {
                return true
            }

            const allValid = this.filteredItems.every(item => {
                const validations = [
                    this.maxValue(item),
                    this.reprocessReasonRule(item),
                    this.destinationProcessRule(item),
                    this.ncCodeRule(item),
                ]

                return validations.every(result => result === true)
            })

            return !allValid
        },
        openConfirmDialog() {
            if (this.$refs.qualityForm && !this.$refs.qualityForm.validate()) {
                return
            }

            this.confirmDialog = true
        },
        closeConfirmDialog() {
            this.confirmDialog = false
        },
        async registerQuality() {
            try {
                this.loading = true
                const itemsToSubmit = this.itemsToSubmit()

                if (itemsToSubmit.length === 0) {
                    return
                }

                const payloadItems = itemsToSubmit.map(entry => ({
                    partNumberId: entry.item.partNumberId,
                    releaseQty: entry.releaseQty,
                    reprocessQty: entry.reprocessQty,
                    rejectQty: entry.rejectQty,
                    destinationProcess: entry.destinationProcessId,
                    reprocessReason: entry.reprocessReasonId,
                    NCcode: entry.ncCodeId,
                }))

                await API.registerBatchQualityPerProcess({
                    items: payloadItems,
                    workOrderId: this.workOrder.id,
                    selectedProcessId: this.selectedProcess?.id,
                    settingId: this.settingProcessesId,
                })

                this.confirmDialog = false
                this.$emit('closeDialog')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('closeDialog')
        },
        onResize() {
            this.height = window.innerHeight - 260
        },
        resetProcessValidations() {
            if (this.$refs.qualityForm) {
                this.$refs.qualityForm.resetValidation()
            }
            this.valid = false

            window.removeEventListener('resize', this.onResize)
            window.addEventListener('resize', this.onResize)
        },
        handleProcessChange() {
            if (!this.selectedProcess?.id) {
                if (this.$refs.qualityForm) {
                    this.$refs.qualityForm.resetValidation()
                }
                return
            }
            this.filteredItems.forEach(item => {
                const assignment =
                    item.processAssignments[this.selectedProcess.id]

                if (!this.shouldShowReleaseField(item) && assignment) {
                    assignment.releaseQty = undefined
                }
            })
        },
        setAllAvailableAsReleased() {
            if (!this.selectedProcess?.id) {
                return
            }
            this.filteredItems.forEach(item => {
                const assignment =
                    item.processAssignments[this.selectedProcess.id]
                if (!assignment) {
                    return
                }
                if (this.setAvailableAsReleased) {
                    const pendingQty = this.getPendingQty(item)
                    assignment.releaseQty = pendingQty
                    assignment.reprocessQty = undefined
                    assignment.rejectQty = undefined
                    assignment.reprocessReason = undefined
                    assignment.destinationProcess = undefined
                    assignment.NCcode = undefined
                } else {
                    assignment.releaseQty = undefined
                }
            })
        },
        verifyCheckBox() {
            if (!this.showReleaseColumn || !this.selectedProcess?.id) {
                this.setAvailableAsReleased = false
                return
            }
            this.setAvailableAsReleased = this.filteredItems.every(item => {
                const assignment =
                    item.processAssignments[this.selectedProcess.id]
                if (!assignment) {
                    return false
                }
                const pendingQty = this.getPendingQty(item)
                return (
                    pendingQty > 0 &&
                    Number(assignment.releaseQty || 0) === pendingQty
                )
            })
        },
    },
}
</script>

<style></style>
